import { Moment } from 'moment';
import { TSelectedPacket } from 'pages/private/Animals/AnimalDetails/AnimalDocumentationBlock/DocumentsTabContent/DocumentsTabContent.types';
import { TSADocPacketGet, TSADocPacketGetUI } from './animalTraining.types';
import { TDocPacketGet, TDocPacketGetUI } from './document.types';
import {
  EApplicationStatus,
  EApplicationStatusUi,
  EGender,
  EGenderUi,
  TExpirationDateType,
  TFileDataType,
  TOptionType,
  TOptionTypePredefinedDB,
  TOptionTypeUi,
  TPolicyUI,
  TPropertyUI,
  TStatusChangeInfoDB,
  TStatusChangeInfoUI,
  TStatusInfoWithSourceUI,
  TUnitUI,
} from './general.types';
import { EVerificationStatus, EVerificationStatusUi, TYesNoOptionType } from './generalTypes';
import {
  EAnimalApplicationStatusDB,
  EAnimalApplicationStatusUI,
  EAnimalResidencyStatusDB,
  EAnimalResidencyStatusUI,
  EAnimalResidencyTagDB,
  EAnimalResidencyTagUI,
  EAnimalRestrictionStatusDB,
  EAnimalRestrictionStatusUI,
  EAnimalRestrictionTagDB,
  EAnimalRestrictionTagUI,
  EPooCompaniesDB,
  EResidencyStatusDB,
  EResidencyStatusUI,
  EResidentTagsDB,
  EResidentTagsUI,
  TCompanyDB,
  TCompanyUi,
  TResidenciesOptionsUI,
  TTenantDeactivationStatusUI,
} from './residence.types';
import { TAnimalVetRecord, TAnimalVetRecordUi, TVetRecordTypeUI } from './vetRecord.types';
import { TStatusChangeHistoryDB, TStatusChangeHistoryUI } from './application.types';

export enum EAnimalTypes {
  ESA = 'ESA',
  PET = 'PET',
  SA = 'SA',
}

export enum EAnimalTypesUI {
  ESA = 'ESA',
  PET = 'PET',
  SA = 'SA',
}

export type TPropertyDB = {
  name: string;
  propertyId: string;
};

export enum EAnimalStatusDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
  MOVED_OUT = 'MOVED_OUT',
  ARCHIVED = 'ARCHIVED',
  RETRACTED = 'RETRACTED',
  MOVED_IN = 'MOVED_IN',
  CONDITIONALLY_APPROVED = 'CONDITIONALLY_APPROVED',
  CONDITIONALLY_MOVED_IN = 'CONDITIONALLY_MOVED_IN',
  ABANDONED = 'ABANDONED',
}

export enum EAnimalStatusUI {
  PENDING = 'Pending',
  APPROVED = 'Future Animal',
  NOT_APPROVED = 'Not Approved',
  MOVED_OUT = 'Moved Out',
  ARCHIVED = 'Archived',
  RETRACTED = 'Retracted',
  MOVED_IN = 'Moved In',
  CONDITIONALLY_APPROVED = 'Conditionally Approved',
  CONDITIONALLY_MOVED_IN = 'Conditionally Moved In',
  ABANDONED = 'Abandoned',
}

export enum EAuthorizationStatus {
  AUTHORIZED = 'AUTHORIZED',
}

export enum EAuthorizationStatusUi {
  AUTHORIZED = 'Authorized',
}

export enum EResidenceStatusDB {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  NOT_APPROVED = 'NOT_APPROVED',
  MOVED_OUT = 'MOVED_OUT',
  ARCHIVED = 'ARCHIVED',
  RETRACTED = 'RETRACTED',
  FORMER_RESIDENT = 'FORMER_RESIDENT',
}

export enum EResidenceStatusUI {
  PENDING = 'Pending',
  APPROVED = 'Aproved',
  NOT_APPROVED = 'Not Approved',
  MOVED_OUT = 'Moved out',
  ARCHIVED = 'Archived',
  RETRACTED = 'Retracted',
  FORMER_RESIDENT = 'Former Resident',
}

export enum EPictureOverviewStatusUI {
  MISSING = 'MISSING',
  SPECIFIED = 'SPECIFIED',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum EPictureOverviewStatusDB {
  MISSING = 'MISSING',
  SPECIFIED = 'SPECIFIED',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum EPolicyOverviewStatusDB {
  MISSING = 'MISSING',
}

export enum EPolicyOverviewStatusUI {
  MISSING = 'MISSING',
}

export enum EVetRecordOverviewStatusUI {
  HAS_VERIFIED = 'HAS_VERIFIED',
  HAS_UNVERIFIED = 'HAS_UNVERIFIED',
  MISSING = 'MISSING',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum EVetRecordOverviewStatusDB {
  HAS_VERIFIED = 'HAS_VERIFIED',
  HAS_UNVERIFIED = 'HAS_UNVERIFIED',
  MISSING = 'MISSING',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export enum EDocsOverviewStatusUI {
  HAS_VERIFIED = 'HAS_VERIFIED',
  HAS_UNVERIFIED = 'HAS_UNVERIFIED',
  MISSING = 'MISSING',
}

export enum EDocsOverviewStatusDB {
  HAS_VERIFIED = 'HAS_VERIFIED',
  HAS_UNVERIFIED = 'HAS_UNVERIFIED',
  MISSING = 'MISSING',
}

export type TAnimalResidenceFilterDB = {
  propertyName: string;
  residenceIds: string[];
  residentName: string;
  uniNumber: string;
};

export type TAnimalResidenceFilterUI = {
  id: string;
};
export type TResidentProfileDB = {
  name: string;
  residentId: string;
};

export type TResidentProfileUI = {
  name: string;
  residentId: string;
};

export type TAnimalUnitDB = {
  number: string;
  property: TPropertyDB;
};

export type TAnimalUnitUI = {
  number: string;
  property: TPropertyDataUi;
};

export type TAnimalResidenceDB = {
  dnaEnabled: boolean | null;
  leaseToDate: string | null;
  moveInDate: string | null;
  policyId: string | null;
  residenceId: string;
  residentProfile: TResidentProfileDB;
  status: EResidenceStatusDB | null;
  unit: TAnimalUnitDB;
  tenantResidencyStatus: EResidencyStatusDB;
  tenantResidencyMoveOutDate: string | null;
  tenantResidencyMoveInDate: string | null;
};

export type TAnimalResidenceUI = {
  dnaEnabled: boolean | null;
  leaseToDate: string | null;
  moveInDate: string | null;
  policyId: string | null;
  residenceId: string;
  residentProfile: TResidentProfileUI;
  status: EResidenceStatusUI | null;
  unit: TAnimalUnitUI;
  tenantResidencyStatus: EResidencyStatusUI;
  tenantResidencyMoveOutDate: string | null;
  tenantResidencyMoveInDate: string | null;
};

export type TInfoDNA = {
  isDnaRequired: boolean;
  isDnaCollected: boolean;
  dnaNumber: string | null;
  dnaCompany: TCompanyDB | null;
};

export type TInfoDNAUi = {
  isDnaRequired: boolean;
  isDnaCollected: boolean;
  dnaNumber: string | null;
  dnaCompany: TCompanyUi | null;
};

export type TDnaInfoDB = {
  company: EPooCompaniesDB;
  companyCustomName: string | null;
  dnaNumber: string | null;
  isCollected: false;
};

export type TDnaInfoUI = {
  company: { label: string | null; value: EPooCompaniesDB };
  companyCustomName?: string;
  dnaNumber: string;
  isCollected: false;
};

export type TAnimalWeightDB = {
  unit: string;
  value: number;
};

export type TAnimalWeightUI = {
  unit: string;
  value: number;
};

export enum ENotBeingChargedReasonDB {
  PET_FEE = 'PET_FEE',
  PET_RENT = 'PET_RENT',
}

export enum ENotBeingChargedReasonUI {
  PET_FEE = 'No pet fee charged',
  PET_RENT = 'No pet rent charged',
}

export enum EConversionStatusUI {
  NONE = 'None',
  PENDING_SA = 'Pending SA',
  PENDING_ESA = 'Pending ESA',
}

export enum EConversionStatusDB {
  NONE = 'NONE',
  PENDING_SA = 'PENDING_SA',
  PENDING_ESA = 'PENDING_ESA',
}

export type TAnimalPetRentDateDB = {
  startDate?: string;
  endDate?: string;
  startedAtOnboarding: boolean;
};
export type TAnimalPetRentDateUI = {
  startDate?: string;
  endDate?: string;
  startedAtOnboarding: boolean;
};

// Both halves of the split animal status, nested beside the legacy combined status field
// exactly as the backend sends it.
export type TAnimalStatusDetailsDB = {
  applicationStatus: EAnimalApplicationStatusDB | null;
  residencyTag: EAnimalResidencyTagDB | null;
  applicationId: string | null;
  conditional: boolean;
  // Set only while the status is Approved until Renewal.
  approvedUntilRenewalDate: string | null;
};

// Nested on the details page rather than flattened as the table does it: the detail already
// carries a residencyStatus of the legacy vocabulary, and two fields named for the same idea
// would invite reading the wrong one.
export type TAnimalStatusDetailsUI = {
  applicationStatus: EAnimalApplicationStatusUI | null;
  residencyTag: EAnimalResidencyTagUI | null;
  applicationId: string | null;
  conditional: boolean;
  approvedUntilRenewalDate: string | null;
};

export type TAnimalDB = {
  animalConversionStatus: EConversionStatusDB;
  id: string;
  name: string;
  animalVerificationStatus: EVerificationStatus;
  birthday: string;
  breed: TOptionType | null;
  species: TOptionType | null;
  primaryColor: TOptionTypePredefinedDB | null;
  deleted: boolean;
  dnaFeatureEnabled: boolean;
  dnaInfo: TDnaInfoDB;
  propertyName: string;
  profilePicture: string;
  isPropertyCustom: boolean;
  type: EAnimalTypes;
  unitNumber: string;
  status: EAnimalStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  weight: TAnimalWeightDB | null;
  residence: TAnimalResidenceDB | null;
  statusChangeInfo: TStatusChangeInfoDB | null;
  existsNonApprovedAnimalApplication: boolean;
  submissionDate: string;
  tags: EAnimalTagsDB[];
  discoveredTagReason: string | null;
  secondaryColor1: TOptionTypePredefinedDB | null;
  secondaryColor2: TOptionTypePredefinedDB | null;
  yardiId: string | null;
  yardiPropertyId: string | null;
  unitPaidPetRent: number | null;
  unitPaidPetFee: number | null;
  unitPetCount: number | null;
  tenantLeaseStartDate: string;
  tenantLeaseEndDate: string;
  isToPetConverted: boolean;
  tenantPhone: string | null;
  tenantEmail: string | null;
  animalPresenceStatus: string;
  isEmployeeAnimal: boolean;
  animalMoveOutDate?: string;
  animalRestrictionStatus: string;
  animalRestrictionTags: string[];
  petFeeChargingStatus: EAnimalCharginStatusDB;
  petFeeChargingStatusChangeInfo: TStatusInfoWithSourceDB | null;
  petRentChargingStatus: EAnimalCharginStatusDB;
  petRentChargingStatusChangeInfo: TStatusInfoWithSourceDB | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
  petRentDates: TAnimalPetRentDateDB[];
};

export type TAnimalUI = {
  animalConversionStatus: EConversionStatusUI;
  id: string;
  name: string;
  animalVerificationStatus: EVerificationStatusUi;
  birthday: string;
  breed: TOptionTypeUi | null;
  species: TOptionTypeUi | null;
  primaryColor: TOptionTypeUi | null;
  deleted: boolean;
  dnaFeatureEnabled: boolean;
  dnaInfo: TDnaInfoUI | null;
  propertyName: string;
  profilePicture: string;
  isCustom: boolean;
  type: EAnimalTypesUI;
  unitNumber: string;
  status: EAnimalStatusUI;
  applicationStatus: EAnimalApplicationStatusUI | null;
  approvedUntilRenewalDate: string | null;
  residencyTag: EAnimalResidencyTagUI | null;
  weight: TAnimalWeightUI | null;
  residence: TAnimalResidenceUI | null;
  firstLetters: string;
  statusChangeInfo: TStatusChangeInfoUI | null;
  existsNonApprovedAnimalApplication: boolean;
  isApproved: string;
  submitionDate: string | null;

  tags: EAnimalTagsUI[];
  discoveredTagReason: string | null;
  secondaryColor1: TOptionTypeUi | null;
  secondaryColor2: TOptionTypeUi | null;
  yardiId: string | null;
  yardiPropertyId: string | null;
  unitPaidPetRent: string;
  unitPaidPetFee: string;
  unitPetCount: number | null;
  isToPetConverted: boolean;
  tenantPhone: string | null;
  tenantEmail: string | null;
  animalPresenceStatus: string;
  isEmployeeAnimal: string;
  animalMoveOutDate?: string;
  animalRestrictionStatus: string;

  petFeeChargingStatus: EAnimalCharginStatusUI;
  petFeeChargingStatusChangeInfo: TStatusInfoWithSourceUI | null;
  petRentChargingStatus: EAnimalCharginStatusUI;
  petRentChargingStatusChangeInfo: TStatusInfoWithSourceUI | null;
  paymentStatus: EAnimalPaymentStatusUI | null;
  petRentDates: TAnimalPetRentDateUI[];
};

export type TAnimalTerminateDB = {
  residenceId: string;
  animalResidencyId: string;
  reason: string;
};

export type TAnimalTerminateUI = {
  residenceId: string;
  animalResidencyId: string;
  reason: string;
};

export type TAggressiveBehaviorDB = {
  acted: boolean;
  incidentDetails?: string;
};

export type TAggressiveBehaviorUI = {
  acted: string;
  incidentDetails?: string;
};

export type TAnimalInfoPropDB = {
  value: string;
  predefinedId?: string;
  predefined?: boolean;
};
export type TAnimalInfoPropUI = {
  value: string;
  predefinedId?: string;
  predefined?: boolean;
};

export type TAnimalBasicInfo = {
  birthDate: string;
  breed: TAnimalInfoPropDB | null;
  gender: EGender;
  id: string;
  isHouseTrained: boolean | null;
  isReproductive: boolean | null;
  name: string;
  photoGallery: string[];
  primaryColor: TAnimalInfoPropDB | null;
  profilePicture: string | null;
  secondaryColor1: TAnimalInfoPropDB | null;
  secondaryColor2: TAnimalInfoPropDB | null;
  species: TAnimalInfoPropDB | null;
  verificationStatus: EVerificationStatus;
  type: EAnimalTypes;
  weight: TAnimalWeightDB | null;
  aggressiveBehavior: TAggressiveBehaviorDB | null;
  isRequiredBecauseOfDisability?: boolean;
  trainedFor?: string;
  microchip: string | null;
  editAllowed: boolean;
  missingSpeciesReason: string | null;
};

export type TAnimalBasicInfoUi = {
  birthDate: string | null;
  breed: TAnimalInfoPropUI | null;
  gender: EGenderUi;
  id: string;
  isHouseTrained: boolean | null;
  reproduction: boolean | null;
  name: string;
  photoGallery: string[];
  primaryColor: TAnimalInfoPropUI | null;
  profilePicture: string | null;
  secondaryColor1: TAnimalInfoPropUI | null;
  secondaryColor2: TAnimalInfoPropUI | null;
  species: TAnimalInfoPropUI | null;
  verificationStatus: EVerificationStatusUi;
  type: EAnimalTypesUI;
  weight: TAnimalWeightUI | null;
  aggressiveBehavior: TAggressiveBehaviorUI | null;
  isRequiredBecauseOfDisability?: boolean;
  trainedFor?: string;
  microchip: string | null;
  isEditAllowed: boolean;
  missingSpeciesReason: string | null;
};

export type TProperty = {
  propertyId: string;
  name: string;
};

export type TPropertyDataUi = {
  propertyId: string;
  name: string;
};

export type TUnit = {
  number: string;
  unitId: string;
  property: TProperty;
};

export type TUnitUi = {
  number: string;
  unitId: string;
  property: TPropertyDataUi;
};

export type TAnimalOwnerInfoUI = {
  id: string;
  email: string | null;
  moveInDate: string | null;
  leaseToDate: string | null;
  name: string;
  phoneNumber: string | null;
  policy: TPolicyUI;
  profilePicture: string | null;
  property: TPropertyUI;
  residencyStatus: EResidencyStatusUI;
  unit: TUnitUI;
  residentId: string;
  tags: EResidentTagsUI[];
  isPropertyCustom: boolean;
  deactivationStatusChangeInfo?: TTenantDeactivationStatusUI | null;
  leaseCharges: unknown[] | null; // JSON
  ledgerTransactions: unknown[] | null; // JSON
};

export type TAnimalOwnerInfoDB = {
  id: string;
  email: string | null;
  moveInDate: string;
  leaseToDate: string | null;
  name: string;
  phoneNumber: string | null;
  policyId: string | null;
  policyName: string | null;
  policyState: string | null;
  profilePicture: string | null;
  propertyId: string;
  propertyName: string;
  isPropertyCustom: boolean;
  status: EResidencyStatusDB;
  unitId: string;
  unitName: string;
  residentId: string;
  tags: EResidentTagsDB[];
  dateSigned: string | null;
  isSigned: boolean;
  signedBy: string | null;
  deactivationStatusChangeInfo?: TTenantDeactivationStatusUI | null;
  leaseCharges: unknown[] | null; // JSON
  ledgerTransactions: unknown[] | null; // JSON
};
export type TCurriculumDB = {
  checkpoint?: number;
  isPassed: boolean;
  isRequired: boolean;
  totalCount?: number;
};
export type TCurriculumUI = {
  checkpoint?: number;
  isPassed: boolean;
  isRequired: boolean;
  totalCount?: number;
};

export enum EAnimalMissingBasicInfoDB {
  ANIMAL_PHOTO = 'ANIMAL_PHOTO',
  ANIMAL_BIRTHDAY = 'ANIMAL_BIRTHDAY',
  ANIMAL_SPECIES = 'ANIMAL_SPECIES',
  ANIMAL_BREED = 'ANIMAL_BREED',
  ANIMAL_GENDER = 'ANIMAL_GENDER',
  ANIMAL_COLOR = 'ANIMAL_COLOR',
  ANIMAL_WEIGHT = 'ANIMAL_WEIGHT',
  MICROCHIP_NUMBER = 'MICROCHIP_NUMBER',
  SPAYED_NEUTERED = 'SPAYED_NEUTERED',
  HOUSE_TRAINED = 'HOUSE_TRAINED',
  HISTORY_OF_AGGRESSION = 'HISTORY_OF_AGGRESSION',
}

export enum EAnimalMissingBasicInfoUI {
  ANIMAL_PHOTO = 'Picture',
  ANIMAL_BIRTHDAY = 'Birthday',
  ANIMAL_SPECIES = 'Species',
  ANIMAL_BREED = 'Breed',
  ANIMAL_GENDER = 'Gender',
  ANIMAL_COLOR = 'Color',
  ANIMAL_WEIGHT = 'Weight',
  MICROCHIP_NUMBER = 'Microchip number',
  SPAYED_NEUTERED = 'Spayed/Neutered',
  HOUSE_TRAINED = 'House trained',
  HISTORY_OF_AGGRESSION = 'History of aggression',
}

export type TDataOverviewUI = {
  curriculum?: TCurriculumUI;
  profilePicture: EPictureOverviewStatusUI;
  vetRecord: EVetRecordOverviewStatusUI;
  hpDocs?: EDocsOverviewStatusUI;
  trainerDocs?: EDocsOverviewStatusUI;
  isPolicySigned?: EPolicyOverviewStatusUI;
  missingBasicInformation: {
    criterion: EAnimalMissingBasicInfoUI;
    state: EPictureOverviewStatusUI;
  }[];
  missingDocumentInformation: string[];
};

export type TDataOverviewDB = {
  curriculum?: TCurriculumDB;
  vetRecord: EVetRecordOverviewStatusDB;
  hpDocs?: EDocsOverviewStatusDB;
  trainerDocs?: EDocsOverviewStatusDB;
  isPolicySigned: EPolicyOverviewStatusDB;
  missingBasicInformation: EAnimalMissingBasicInfoDB[];
  missingDocumentInformation: string[];
};

export type TAnimalApplication = {
  applicationStatus: EApplicationStatus;
  id: string;
  leaseStartDate: string;
  leaseToDate: string | null;
  policyId: string | null;
  propertyManagerName: string;
  propertyName: string;
  unitNumber: string;
};

export type TAnimalApplicationUi = {
  applicationStatus: EApplicationStatusUi;
  id: string;
  leaseStartDate: string;
  leaseToDate: string | null;
  policyId: string | null;
  propertyName: string;
  propertyManagerName: string;
  unitNumber: string;
};

export enum EAnimalTagsDB {
  'PNC_PET_FEE' = 'PNC_PET_FEE',
  'PNC_PET_RENT' = 'PNC_PET_RENT',
  'KNOWN' = 'KNOWN',
  'DISCOVERD' = 'DISCOVERED',
  'DISCOVERED_APP' = 'DISCOVERED_APP',
  'DISCOVERED_PAYING' = 'DISCOVERED_PAYING',
}

export enum EAnimalTagsUI {
  'PNC_PET_FEE' = 'PNC Pet Fee',
  'PNC_PET_RENT' = 'PNC Pet Rent',
  'KNOWN' = 'Known',
  'DISCOVERD' = 'Discovered',
  'DISCOVERED_APP' = 'Discovered (App)',
  'DISCOVERED_PAYING' = 'Discovered (Paying)',
}

export type TChargeInformationDB = {
  status: EAnimalCharginStatusDB;
  changeInfo: TStatusInfoWithSourceDB | null;
  changeReason: string | null;
};

export type TChargeInformationUI = {
  status: EAnimalCharginStatusUI;
  changeInfo: TStatusInfoWithSourceUI | null;
  changeReason: string | null;
};

export type TAnimalDetail = {
  hasObservableDisability: boolean | null;
  animal: TAnimalBasicInfo;
  applications: TAnimalApplication[] | null;
  dnaInfo: TInfoDNA;
  ownerInfo: TAnimalOwnerInfoDB;
  vetRecords: TAnimalVetRecord[] | null;
  hpDocPackets: TDocPacketGet[] | null;
  residencyStatus: EAnimalResidencyStatusDB;
  // Both halves of the split status, composed live beside the legacy combined field above.
  statusDetails: TAnimalStatusDetailsDB | null;
  residencyStatusChangeInfo: TStatusChangeInfoDB | null;
  saDocuments: TSADocPacketGet[] | null;
  dataOverview: TDataOverviewDB;
  isToPetConverted: boolean;
  statusChangeHistory: TStatusChangeHistoryDB[];
  policyContent: string | null;
  animalApplicationId: string | null;
  canRevokeMoveIn: boolean;
  notBeingChargedReason: ENotBeingChargedReasonDB | null;
  tags: EAnimalTagsDB[];
  animalMoveInDate: string;
  discoveredTagReason: string | null;
  statusUpdateLocked: boolean;
  isScreeningEnabled: boolean;
  toPetConvertedReason: string | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingMoveInDateReason: string | null;
  toPetConvertedDate: string | null;
  shouldTenantSignAddendum: boolean;
  animalRestrictionStatus: EAnimalRestrictionStatusDB;
  animalRestrictionTags: EAnimalRestrictionTagDB[] | null;
  otherRestriction?: string;
  petRentChargingStatusDetails: TChargeInformationDB | null;
  petFeeChargingStatusDetails: TChargeInformationDB | null;
  ticketId: string | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
};

export type TAnimalDetailUi = {
  hasObservableDisability: boolean | null;
  animal: TAnimalBasicInfoUi;
  applications: TAnimalApplicationUi[] | null;
  dnaInfo: TInfoDNAUi;
  ownerInfo: TAnimalOwnerInfoUI;
  vetRecords: TAnimalVetRecordUi[] | null;
  hpDocPackets: TDocPacketGetUI[] | null;
  residencyStatus: EAnimalResidencyStatusUI;
  splitStatus: TAnimalStatusDetailsUI;
  residencyStatusChangeInfo: TStatusChangeInfoUI | null;
  saDocuments: TSADocPacketGetUI[] | null;
  dataOverview: TDataOverviewUI;
  isToPetConverted: boolean;
  statusChangeHistory: TStatusChangeHistoryUI[];
  policyContent: Blob | null;
  pendingAnimalApplicationId: string | null;
  canRevokeMoveIn: boolean;
  notBeingChargedReason: ENotBeingChargedReasonUI | null;
  animalMoveInDate: string;
  tags: EAnimalTagsUI[];
  discoveredTagReason: string | null;
  statusUpdateLocked: boolean;
  isScreeningEnabled: boolean;
  toPetConvertedReason: string | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingMoveInDateReason: string | null;
  toPetConvertedDate: string | null;
  shouldTenantSignAddendum: boolean;
  animalRestrictionStatus: EAnimalRestrictionStatusUI;
  animalRestrictionTags: EAnimalRestrictionTagUI[];
  otherRestriction?: string;
  petRentChargingStatusDetails: TChargeInformationUI | null;
  petFeeChargingStatusDetails: TChargeInformationUI | null;
  ticketId: string | null;
  paymentStatus: EAnimalPaymentStatusUI | null;
};

export type TAnimalStatisticUI = {
  fromDate: string;
  totalCount: number;
};

export type TAnimalStatisticDB = {
  fromDate: string;
  totalCount: number;
};

export type TAnimalsStatisticsUI = {
  addedPets: TAnimalStatisticUI;
  notPaidAnimalsCount: number | null;
  paidAnimalsCount: number | null;
  hasPetProfileFeeStatistics: boolean;
};

export type TAnimalsStatisticsDB = {
  addedPets: TAnimalStatisticDB;
  notPaidAnimalsCount: number | null;
  paidAnimalsCount: number | null;
};

export enum TChangerStatusRolesDB {
  ADMIN = 'ADMIN',
  LANDLORD = 'LANDLORD',
  PROPERTY_MANAGER = 'PROPERTY_MANAGER',
}

export enum TChangerStatusRolesUI {
  ADMIN = 'admin',
  LANDLORD = 'landlord',
  PROPERTY_MANAGER = 'propertyManager',
}

export type TApplicantAnimalDB = {
  id: string;
  type: EAnimalTypes;
  applicationStatus: EApplicationStatus;
  statusChangerRole: TChangerStatusRolesDB | null;
  statusChangerName: string | null;
  rejectReason: string | null;
  moveInDate: string;
  submittedDate: string;
  residentName: string;
  petName: string;
  propertyName: string;
  unitName: string;
  animalVerificationStatus: EVerificationStatus;
  statusChangeInfo: TStatusChangeInfoDB | null;
  animalProfilePicture: string | null;
};

export type TApplicantAnimalUI = {
  id: string;
  type: EAnimalTypesUI;
  applicationStatus: EApplicationStatusUi;
  statusChangerRole: TChangerStatusRolesUI | null;
  statusChangerName: string | null;
  rejectReason: string | null;
  moveInDate: string | null;
  submittedDate: string | null;
  residentName: string;
  petName: string;
  propertyName: string;
  unitName: string;
  animalVerificationStatus: EVerificationStatusUi;
  statusChangeInfo: TStatusChangeInfoUI | null;
  animalProfilePicture: string | null;
};

export type TDuplicateAnimalDB = {
  animalId: string;
  animalName: string;
  animalSpecie: TOptionType | null;
  animalType: EAnimalTypes;
  ownerName: string;
  ownerUsername: string;
  status: EApplicationStatus;
};
export type TDuplicateAnimalUI = {
  animalId: string;
  animalName: string;
  animalType: EAnimalTypesUI;
  ownerName: string;
  ownerUsername: string;
  status: EApplicationStatusUi;
};

export type TCheckDuplicateAnimalDB = {
  animalName: string;
  residenceId: string;
};
export type TCheckDuplicateAnimalUI = {
  animalName: string;
  residenceId: string;
};

export enum EAnimalPaymentStatusDB {
  PAID = 'PAID',
  MISSING = 'MISSING',
  MISSING_RENEWAL = 'MISSING_RENEWAL',
  SCHEDULED_RENEWAL = 'SCHEDULED_RENEWAL',
}

export enum EAnimalPaymentStatusUI {
  PAID = 'Paid',
  MISSING = 'Missing',
  MISSING_RENEWAL = 'Missing-Renewal',
  SCHEDULED_RENEWAL = 'Scheduled-Renewal',
}

export enum EAnimalCharginStatusDB {
  UNSPECIFIED = 'UNSPECIFIED',
  BEING_CHARGED = 'BEING_CHARGED',
  NOT_BEING_CHARGED = 'NOT_BEING_CHARGED',
  SHOULD_NOT = 'SHOULD_NOT',
  GRACE_PERIOD = 'GRACE_PERIOD',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  NO_PET_FEE = 'NO_PET_FEE',
  NO_PET_RENT = 'NO_PET_RENT',
}

export enum EAnimalCharginStatusUI {
  UNSPECIFIED = 'Unspecified',
  BEING_CHARGED = 'Being charged',
  NOT_BEING_CHARGED = 'Not being charged',
  SHOULD_NOT = 'Shouldn’t be charged',
  GRACE_PERIOD = 'Grace period',
  NOT_APPLICABLE = 'Not Applicable',
  NO_PET_FEE = 'No pet fee',
  NO_PET_RENT = 'No pet rent',
}

export type TGenderUI = { gender: EGenderUi; neutered: TYesNoOptionType };
export type TGender = { type: EGender; isReproductive?: boolean | null };

export type TAnimalSpecieDB = {
  value?: string;
  id?: string | null;
};
export type TAnimalSpecieUI = {
  label: string;
  value: string;
};

export type TAnimalColorsDB = {
  value?: string;
  id: string | null;
};
export type TAnimalColorsUI = {
  label: string;
  value: string;
  __isNew__: boolean;
};

export type TAnimalBreedDB = {
  value?: string;
  id?: string | null;
};
export type TAnimalBreedUI = {
  label: string;
  value: string;
};

export type TCreateAnimalDB = {
  birthDate: string | null;
  breed: TAnimalBreedDB;
  name: string | null;
  primaryColor: TAnimalColorsDB;
  residencyId: string;
  species: TAnimalSpecieDB;
  weight: string | null;
  status: EAnimalResidencyStatusDB;
  moveInDate: string | null;
  animalType: EAnimalTypes;
  secondaryColor1: string | null;
  secondaryColor2: string | null;
  profilePicture?: string | null;
  gender: TGender | null;
  houseTrained?: boolean | null;
  requiredBecauseOfDisability?: string;
  trainedFor?: string;
  aggressiveBehavior: {
    acted?: boolean | null;
    incidentDetails?: string;
  };
  microchip?: string;
  needToScreen: boolean;
};

export type TCreateAnimalUI = {
  birthDate: Moment;
  breed: TAnimalBreedUI;
  name: string | null;
  primaryColor: TAnimalColorsUI;
  residencyId: TResidenciesOptionsUI;
  species: TAnimalSpecieUI;
  weight: string;
  status: EAnimalResidencyStatusUI;
  moveInDate?: Moment;
  secondaryColor1: TOptionTypeUi;
  secondaryColor2: TOptionTypeUi;
  profilePicture: { url: string } | null;
  gender: EGenderUi | null;
  neutered: TYesNoOptionType;
  houseTrained: TYesNoOptionType;
  animalType: { value: EAnimalTypesUI };
  isAggressive: TYesNoOptionType;
  aggressionInfo?: string;
  microchip?: string;
  requiredBecauseOfDisability?: string;
  trainedFor?: string;
  needToScreen: boolean;
};

export type TCreateAnimalResponseDB = {
  movedIn: boolean;
  animalResidencyId: string | null;
  applicationId: string | null;
};
export type TCreateAnimalResponseUI = {
  isMovedIn: boolean;
  id: string;
};

export type TStatusInfoWithSourceDB = TStatusChangeInfoDB & {
  exportDate?: string;
  name?: string;
  url?: string;
  moveOutDate?: string;
};

export type TUpdateAnimalNameDB = {
  animalId: string;
  name: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalNameUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalSpeciesDB = {
  animalId: string;
  animalSpecies: { id?: string; value?: string };
  tenantResidencyId: string;
};
export type TUpdateAnimalSpeciesUI = {
  id: string;
  animalSpecies: TOptionTypeUi;
  tenantResidencyId: string;
};

export type TUpdateAnimalWeightDB = {
  animalId: string;
  unit: string;
  weight: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalWeightUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalBirthDateDB = {
  animalId: string;
  birthDate: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalBirthDateUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalMicrochipDateDB = {
  animalId: string;
  microchip: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalMicrochipDateUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalReproductiveDB = {
  animalId: string;
  reproductive: boolean;
  tenantResidencyId: string;
};
export type TUpdateAnimalReproductiveUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalHouseTrainedDB = {
  animalId: string;
  houseTrained: boolean;
  tenantResidencyId: string;
};
export type TUpdateAnimalHouseTrainedUI = {
  id: string;
  value: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalGenderDB = {
  animalId: string;
  gender: EGender;
  tenantResidencyId: string;
};
export type TUpdateAnimalGenderUI = {
  id: string;
  value: EGenderUi;
  tenantResidencyId: string;
};

export type TUpdateAnimalBreedDB = {
  animalId: string;
  animalBreed: { id?: string; value?: string };
  tenantResidencyId: string;
};
export type TUpdateAnimalBreedUI = {
  id: string;
  breed: TOptionTypeUi;
  tenantResidencyId: string;
};

export type TUpdateAnimalAggressiveBehaviorDB = {
  animalId: string;
  aggressiveBehavior: { acted: boolean; incidentDetails?: string };
  tenantResidencyId: string;
};
export type TUpdateAnimalAggressiveBehaviorUI = {
  id: string;
  aggressiveBehavior: { acted: boolean; incidentDetails?: string };
  tenantResidencyId: string;
};

export type TUpdateAnimalTrainedForDB = {
  animalId: string;
  trainedFor: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalTrainedForUI = {
  id: string;
  trainedFor: string;
  tenantResidencyId: string;
};

export type TUpdateAnimalColorsDB = {
  animalId: string;
  primaryColor?: {
    value?: string;
    id?: string | null;
  };
  secondaryColor1?: string;
  secondaryColor2?: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalColorsUI = {
  id: string;
  primaryColor?: TOptionTypeUi;
  secondaryColor1?: TOptionTypeUi;
  secondaryColor2?: TOptionTypeUi;
  tenantResidencyId: string;
};

export type TUpdateAnimalProfilePictureDB = {
  animalId: string;
  profilePicture: string;
  tenantResidencyId: string;
};
export type TUpdateAnimalProfilePictureUI = {
  id: string;
  profilePicture: string;
  tenantResidencyId: string;
};

export type TVaccinationTypeDB = {
  predefined: boolean;
  value: string;
};
export type TVaccinationTypeUI = {
  predefined?: boolean;
  value?: string;
};

export type TVaccinationExpirationDB = {
  expirable: boolean;
  date?: string;
  expired: boolean;
};

export type TVaccinationExpirationUI = {
  expirable: boolean;
  date: string;
  expired: boolean;
};

export type TChangedRecordsTypeDB = {
  predefined?: string;
  custom?: string;
  value?: string;
};

export type TChangedRecordsTypeUI = {
  predefined?: string;
  custom?: string;
  value?: string;
};

export type TVaccinationRecordDB = {
  type: TVaccinationTypeDB;
  expiration: TVaccinationExpirationDB | null;
  id?: string;
  isExpired?: boolean;
};

export type TVaccinationRecordUI = {
  type: TVaccinationTypeUI;
  expiration: TVaccinationExpirationDB | null;
  id?: string;
  isExpired?: boolean;
};
export type TNewVaccinationRecordDB = {
  type: TChangedRecordsTypeDB;
  expiration: TVaccinationExpirationDB | null;
  id?: string;
  isExpired?: boolean;
};

export type TNewVaccinationRecordUI = {
  type: TChangedRecordsTypeUI;
  expiration: TVaccinationExpirationDB | null;
  id?: string;
  isExpired?: boolean;
};

export type TVetRecordsDB = {
  type: TVetRecordTypeUI;
  expiration?: TExpirationDateType;
};
export type TVetRecordsUI = {
  type: TVetRecordTypeUI;
  expiration?: TExpirationDateType;
};

export type TAnimalVetRecordCreateUi = {
  animalId: string;
  files: TFileDataType[];
  docName: string;
  records: TVetRecordsUI[];
  tenantResidencyId: string;
};

export type TAnimalVetRecordCreate = {
  animalId: string;
  records: TVetRecordsDB[];
  files: TFileDataType[];
  docName: string;
  tenantResidencyId: string;
};

export type TVetRecordDocumentsDB = {
  animalId: string;
  vetRecordId: string;
  newFiles: TFileDataType[];
  removedDocumentsId: string[];
  removedRecordIds: string[];
  newRecords: TNewVaccinationRecordDB[];
  changedRecords: TNewVaccinationRecordDB[];
  tenantResidencyId: string;
};

export type TVetRecordDocumentsUI = {
  animalId: string;
  vetRecordId: string;
  newFiles: TFileDataType[];
  removedDocumentsId: string[];
  removedRecordIds: string[];
  newRecords: TVaccinationRecordUI[];
  changedRecords: TVaccinationRecordUI[];
  tenantResidencyId: string;
};

export type TVetRecordDeleteUI = {
  animalId: string;
  vetRecordId: string;
  tenantResidencyId: string;
};

export type TVetRecordDeleteDB = {
  animalId: string;
  vetRecordId: string;
  tenantResidencyId: string;
};

export type TResidentAnimalScreeningUI = {
  tenantResidencyId: string;
  animalResidencyId: string;
  needToScreen: boolean;
};

export type TResidentAnimalScreeningDB = {
  tenantResidencyId: string;
  animalResidencyId: string;
  needToScreen: boolean;
};

export type TConditionalMovedInApprovedUI = {
  tenantResidencyId: string;
  animalResidencyId: string;
};

export type TConditionalMovedInApprovedDB = {
  tenantResidencyId: string;
  animalResidencyId: string;
};

export type TDownloadAnimalDocumnetsDB = {
  tenantResidencyId: string;
  animalId: string;
  hpDocumentPacketIds: string[];
  vetRecordIds: string[];
  saDocumentPacketIds: string[];
};

export type TDownloadAnimalDocumnetsUI = {
  tenantResidencyId: string;
  animalId: string;
  documnets: TSelectedPacket[];
  animalName: string;
};

export type TCreateAnimalIncompleteUI = {
  name: string;
  type: EAnimalTypesUI;
  status: EAnimalStatusUI;
  species: TOptionTypeUi | null;
  moveInDate: string;
  missingSpeciesReason?: string;
  tenantResidencyId: string;
};

export type TCreateAnimalIncompleteDB = {
  name: string;
  type: EAnimalTypes;
  species: TOptionType | null;
  status: EAnimalStatusDB;
  moveInDate: string;
  missingSpeciesReason?: string;
  tenantResidencyId: string;
};

export type TCreateAnimalIncompleteResponseDB = {
  animalId: string | null;
  applicationId: string | null;
  animalType: EAnimalTypes;
  animalResidencyId: string | null;
  defaultNeedToScreen: boolean;
  movedIn: boolean;
  birthDate: string | null;
  microchip?: string;
  breed: TAnimalInfoPropDB | null;
  primaryColor: TOptionTypePredefinedDB | null;
  secondaryColor1: TAnimalInfoPropDB | null;
  secondaryColor2: TAnimalInfoPropDB | null;
  weight: TAnimalWeightDB | null;
  gender: TGender | null;
  isHouseTrained: boolean | null;
  aggressiveBehavior: TAggressiveBehaviorDB | null;
  trainedFor?: string;
  species: TAnimalInfoPropDB | null;
};

export type TCreateAnimalIncompleteResponseUI = {
  animalId: string | null;
  applicationId: string | null;
  animalType: EAnimalTypesUI;
  animalResidencyId: string | null;
  defaultNeedToScreen: boolean;
  movedIn: boolean;
  birthDate: string | null;
  microchip?: string;
  breed: TAnimalBreedUI | null;
  primaryColor: TAnimalColorsUI | null;
  secondaryColor1: TOptionTypeUi | null;
  secondaryColor2: TOptionTypeUi | null;
  weight: string | null;
  gender: EGenderUi | null;
  neutered: TYesNoOptionType;
  isHouseTrained: TYesNoOptionType;
  isAggressive: TYesNoOptionType;
  aggressionInfo?: string;
  trainedFor?: string;
  species: TAnimalInfoPropUI | null;
};

export type TCompleteAnimalDB = {
  animalId: string;
  tenantResidencyId: string;
  applicationId?: string | null;
  animalResidencyId?: string | null;
  profilePicture?: string | null;
  name: string;
  birthDate?: string | null;
  species: TOptionType | null;
  breed: TAnimalBreedDB | null;
  primaryColor: TAnimalColorsDB | null;
  secondaryColor1?: string | null;
  secondaryColor2?: string | null;
  microchip?: string;
  weight: string | null;
  gender: TGender | null;
  houseTrained?: boolean;
  aggressiveBehavior: TAggressiveBehaviorDB | null;
  trainedFor?: string;
  needToScreen: boolean;
};
export type TCompleteAnimalUI = {
  animalId: string;
  tenantResidencyId: string;
  applicationId: string | null;
  animalResidencyId: string | null;
  profilePicture: { url: string } | null;
  name: string;
  birthDate: Moment | null;
  species: TOptionTypeUi | null;
  breed: TAnimalBreedUI | null;
  primaryColor: TAnimalColorsUI | null;
  secondaryColor1: TOptionTypeUi;
  secondaryColor2: TOptionTypeUi;
  microchip?: string;
  weight: string | null;
  gender: EGenderUi | null;
  neutered: TYesNoOptionType;
  houseTrained: TYesNoOptionType;
  isAggressive: TYesNoOptionType;
  aggressionInfo?: string;
  trainedFor?: string;
  needToScreen: boolean;
};
